import type { PopperPlacementType } from '@mui/material';
import type { TimeZone } from '@shared/utils/date';
import type { ElementType, MouseEventHandler } from 'react';
import Popper, { type PopperTriggerProps } from '../../Popper';
import type {
  DateRange,
  DateRangePickerInteraction,
  LabeledPreset,
} from '../types';
import { PickerPanel } from './PickerPanel';
import { PickerProvider, usePicker } from './PickerProvider';
import { Trigger, type TriggerProps } from './Trigger';

export interface PickerSlots {
  trigger?: ElementType<TriggerProps>;
}

export interface PickerSlotProps {
  trigger?: Partial<Omit<TriggerProps, 'onClick' | 'open' | 'value'>>;
}

export interface PickerProps {
  maxDurationSeconds?: number;
  onChange: (value: DateRange) => void;
  onInteraction?: (interaction: DateRangePickerInteraction) => void;
  placement?: PopperPlacementType;
  presets?: readonly LabeledPreset[];
  slotProps?: PickerSlotProps;
  slots?: PickerSlots;
  timeZone?: TimeZone;
  value: DateRange;
}

export function Picker({
  maxDurationSeconds,
  onChange,
  onInteraction,
  placement = 'bottom-end',
  presets,
  slotProps,
  slots,
  timeZone,
  value,
}: PickerProps) {
  const TriggerComponent = slots?.trigger ?? Trigger;

  return (
    <PickerProvider
      maxDurationSeconds={maxDurationSeconds}
      onChange={onChange}
      onInteraction={onInteraction}
      presets={presets}
      timeZone={timeZone}
      value={value}
    >
      <PickerPopper
        placement={placement}
        TriggerComponent={TriggerComponent}
        triggerProps={slotProps?.trigger}
      />
    </PickerProvider>
  );
}

interface PickerPopperProps {
  placement: PopperPlacementType;
  TriggerComponent: ElementType<TriggerProps>;
  triggerProps?: PickerSlotProps['trigger'];
}

function PickerPopper({
  placement,
  TriggerComponent,
  triggerProps,
}: PickerPopperProps) {
  const { close } = usePicker();

  return (
    <Popper
      Content={PickerPanel}
      Trigger={PickerTrigger}
      onClose={close}
      placement={placement}
      triggerProps={{
        TriggerComponent,
        triggerProps,
      }}
    />
  );
}

interface PickerTriggerProps extends PopperTriggerProps {
  TriggerComponent: ElementType<TriggerProps>;
  triggerProps?: PickerSlotProps['trigger'];
}

function PickerTrigger({
  onClick,
  TriggerComponent,
  triggerProps,
}: PickerTriggerProps) {
  const { isOpen, open, presets, timeZone, value } = usePicker();

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    if (!isOpen) {
      open();
    }

    onClick(event);
  };

  return (
    <TriggerComponent
      {...triggerProps}
      onClick={handleClick}
      open={isOpen}
      presets={presets}
      timeZone={timeZone}
      value={value}
    />
  );
}
